import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_DIAGNOSES = 'FETCH_DIAGNOSES';
export const FETCH_DIAGNOSES_SUCCESS = 'FETCH_DIAGNOSES_SUCCESS';
export const FETCH_DIAGNOSES_FAIL = 'FETCH_DIAGNOSES_FAIL';
export const CLEAR_DIAGNOSES = 'CLEAR_DIAGNOSES';
export const CREATE_DIAGNOSIS = 'CREATE_DIAGNOSIS';
export const CREATE_DIAGNOSIS_SUCCESS = 'CREATE_DIAGNOSIS_SUCCESS';
export const CREATE_DIAGNOSIS_FAIL = 'CREATE_DIAGNOSIS_FAIL';
export const UPDATE_DIAGNOSIS = 'UPDATE_DIAGNOSIS';
export const UPDATE_DIAGNOSIS_SUCCESS = 'UPDATE_DIAGNOSIS_SUCCESS';
export const UPDATE_DIAGNOSIS_FAIL = 'UPDATE_DIAGNOSIS_FAIL';
export const DELETE_DIAGNOSIS = 'DELETE_DIAGNOSIS';
export const DELETE_DIAGNOSIS_SUCCESS = 'DELETE_DIAGNOSIS_SUCCESS';
export const DELETE_DIAGNOSIS_FAIL = 'DELETE_DIAGNOSIS_FAIL';

// Reducer
export default function diagnosesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_DIAGNOSES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_DIAGNOSES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_DIAGNOSES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_DIAGNOSES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_DIAGNOSIS_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_DIAGNOSIS_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_DIAGNOSIS_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchDiagnoses(payload) {
  return { type: FETCH_DIAGNOSES, payload };
}

export function fetchDiagnosesSuccess(payload) {
  return { type: FETCH_DIAGNOSES_SUCCESS, payload };
}

export function fetchDiagnosesFail(payload) {
  return { type: FETCH_DIAGNOSES_FAIL, payload };
}

export function clearDiagnoses(payload) {
  return { type: CLEAR_DIAGNOSES, payload };
}

export function createDiagnosis(payload) {
  return { type: CREATE_DIAGNOSIS, payload };
}

export function createDiagnosisSuccess(payload) {
  return { type: CREATE_DIAGNOSIS_SUCCESS, payload };
}

export function createDiagnosisFail(payload) {
  return { type: CREATE_DIAGNOSIS_FAIL, payload };
}

export function updateDiagnosis(payload) {
  return { type: UPDATE_DIAGNOSIS, payload };
}

export function updateDiagnosisSuccess(payload) {
  return { type: UPDATE_DIAGNOSIS_SUCCESS, payload };
}

export function updateDiagnosisFail(payload) {
  return { type: UPDATE_DIAGNOSIS_FAIL, payload };
}

export function deleteDiagnosis(payload) {
  return { type: DELETE_DIAGNOSIS, payload };
}

export function deleteDiagnosisSuccess(payload) {
  return { type: DELETE_DIAGNOSIS_SUCCESS, payload };
}

export function deleteDiagnosisFail(payload) {
  return { type: DELETE_DIAGNOSIS_FAIL, payload };
}


// Requests

let abort;

export function requestFetchDiagnoses({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi(`resource/diagnosis/?limit=${limit}&offset=${offset}`);
  abort = request.abort;
  return request.res;
}

export function requestSearchDiagnoses({ filters, limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('resource/diagnosis/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'DX Code',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateDiagnosis(payload) {
  return callApi('resource/diagnosis/', 'POST', payload);
}

export function requestUpdateDiagnosis(payload) {
  return callApi('resource/diagnosis/', 'PATCH', payload);
}

export function requestDeleteDiagnosis(payload) {
  return callApi(`resource/diagnosis/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchDiagnosesSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchDiagnoses, action.payload)
      : yield call(requestFetchDiagnoses, action.payload);
    yield put(fetchDiagnosesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchDiagnosesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createDiagnosisSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateDiagnosis, values);
    yield put(createDiagnosisSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Diagnosis successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createDiagnosisFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateDiagnosisSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateDiagnosis, values);
    yield put(updateDiagnosisSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Diagnosis successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateDiagnosisFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteDiagnosisSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteDiagnosis, ID);
    yield put(deleteDiagnosisSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Diagnosis successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteDiagnosisFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Diagnosis',
      variant: 'warning',
    }));
  }
}

export function* diagnosesSaga() {
  yield takeLatest(FETCH_DIAGNOSES, fetchDiagnosesSaga);
  yield takeEvery(CREATE_DIAGNOSIS, createDiagnosisSaga);
  yield takeEvery(UPDATE_DIAGNOSIS, updateDiagnosisSaga);
  yield takeEvery(DELETE_DIAGNOSIS, deleteDiagnosisSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/diagnoses.js